import { useBizyChatActions } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';

type EvidenceNodeType = 'chart' | 'matrix' | 'table' | 'scorecard';

// Naive singular→plural for the expand affordance's entity noun (an axis or
// column label). Handles the common consonant+y → -ies; the agent tolerates
// the rest. Empty when there's no usable label.
function pluralize(noun?: string): string {
	const n = noun?.trim().toLowerCase();
	if (!n) return '';
	if (/s$/.test(n)) return n;
	if (/[^aeiou]y$/.test(n)) return n.replace(/y$/, 'ies');
	return `${n}s`;
}

/**
 * Evidence-node drill-in / expand behavior shared by the chart, matrix, table,
 * and scorecard nodes. Clicking an element sends a renderer-built scoped
 * follow-up (the agent doesn't author these) via the injected `onDrill`.
 * Disabled in read-only views and while a previous turn is processing.
 */
export function useEvidenceDrill() {
	const { onDrill, isReadOnly, followUpsDisabled } = useBizyChatActions();
	const { track, conversationId, messageId } = useBizyAnalytics();

	const drillEnabled = !isReadOnly && !!onDrill && !followUpsDisabled;

	const drill = (nodeType: EvidenceNodeType, label: string, opts?: { title?: string; variant?: string }) => {
		const entity = label.trim();
		if (!onDrill || !entity) return;
		track(BizyAnalyticsEvent.EvidenceDrilled, {
			nodeType,
			variant: opts?.variant,
			action: 'drill',
			conversationId,
			messageId,
			path: window.location.pathname,
		});
		const verb = nodeType === 'matrix' ? `break down ${entity}` : `tell me more about ${entity}`;
		const title = opts?.title?.trim();
		const message = title ? `In "${title}", ${verb}.` : `${verb.charAt(0).toUpperCase()}${verb.slice(1)}.`;
		onDrill(message);
	};

	const expand = (nodeType: 'chart' | 'matrix' | 'table', count: number, opts?: { title?: string; noun?: string }) => {
		if (!onDrill) return;
		track(BizyAnalyticsEvent.EvidenceDrilled, { nodeType, action: 'expand_groups', conversationId, messageId, path: window.location.pathname });
		const noun = pluralize(opts?.noun) || (nodeType === 'matrix' ? 'rows' : 'groups');
		const title = opts?.title?.trim();
		const message = title ? `In "${title}", show all ${count} ${noun}.` : `Show all ${count} ${noun}.`;
		onDrill(message);
	};

	return { drillEnabled, drill, expand };
}
